import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { selectSeasonValue } from '@/features/points/helpers'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface ConvertPointsPanelProps {
  season1TotalPoints: NormalizedNumber
  season2TotalPoints: NormalizedNumber
  season3TotalPoints: NormalizedNumber
  claimableSpk: NormalizedNumber
  claimedSpk: NormalizedNumber
  variant: 'season-2' | 'season-3'
  convertPointsPanelAction: 'claim' | 'switch-to-mainnet'
  onClaim: (() => void) | undefined
  onSwitchToMainnet: () => void
}

export function ConvertPointsPanel({
  season1TotalPoints,
  season2TotalPoints,
  season3TotalPoints,
  claimableSpk,
  claimedSpk,
  variant,
  convertPointsPanelAction,
  onClaim,
  onSwitchToMainnet,
}: ConvertPointsPanelProps) {
  return (
    <Panel
      variant="secondary"
      spacing="none"
      className="h-fit p-2"
      data-testid={testIds.points.convertPointsPanel.container}
    >
      <div className="flex flex-col gap-1">
        <PointsSummaryPanel
          season2TotalPoints={season2TotalPoints}
          season3TotalPoints={season3TotalPoints}
          variant={variant}
        />
        <DetailsPanel
          season1TotalPoints={season1TotalPoints}
          season2TotalPoints={season2TotalPoints}
          claimableSpk={claimableSpk}
          claimedSpk={claimedSpk}
          variant={variant}
          convertPointsPanelAction={convertPointsPanelAction}
          onSwitchToMainnet={onSwitchToMainnet}
          onClaim={onClaim}
        />
      </div>
    </Panel>
  )
}

function PointsSummaryPanel({
  season2TotalPoints,
  season3TotalPoints,
  variant,
}: Pick<ConvertPointsPanelProps, 'season2TotalPoints' | 'season3TotalPoints' | 'variant'>) {
  const variantSeason = variant === 'season-3' ? 3 : 2
  const label = selectSeasonValue(variantSeason, {
    2: 'Season 2 points',
    3: 'Season 3 points',
  })
  const summaryPoints = selectSeasonValue(variantSeason, {
    2: season2TotalPoints,
    3: season3TotalPoints,
  })

  return (
    <Panel variant="quaternary" className="flex flex-col items-center gap-3 p-10">
      <h6 className="typography-label-2 text-tertiary">{label}</h6>
      <div className="flex items-center gap-0.5 md:gap-1.5">
        <img src={assets.points.pointsIcon} alt="Points Icon" className="size-9" />
        <div className="typography-heading-2 text-primary-inverse">
          <TokenAmountTooltip
            token={USD_MOCK_TOKEN}
            amount={summaryPoints}
            options={{ style: 'compact', showSymbol: 'never' }}
          />
        </div>
      </div>
    </Panel>
  )
}

function DetailsPanel({
  season1TotalPoints,
  season2TotalPoints,
  claimableSpk,
  claimedSpk,
  variant,
  convertPointsPanelAction,
  onClaim,
  onSwitchToMainnet,
}: Omit<ConvertPointsPanelProps, 'season3TotalPoints'>) {
  const seasonPoints =
    variant === 'season-3'
      ? [
          { label: 'Points earned in Season 1', amount: season1TotalPoints },
          { label: 'Points earned in Season 2', amount: season2TotalPoints },
        ]
      : [{ label: 'Points earned in Season 1', amount: season1TotalPoints }]

  return (
    <Panel variant="secondary" spacing="xs" className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 border-fg-primary border-b pb-5 [&:not(:has(*))]:hidden">
        {seasonPoints.map((entry) => (
          <SeasonPointsItem key={entry.label} {...entry} />
        ))}
        {claimableSpk.gt(NormalizedNumber.ZERO) && (
          <DetailsItem>
            <DetailsItemLabel text="Claimable SPK" />
            <DetailsItemValue icon={tokenLogos.spk} iconAlt="SPK icon">
              <TokenAmountTooltip
                token={USD_MOCK_TOKEN}
                amount={claimableSpk}
                options={{ style: 'compact', showSymbol: 'never' }}
              />
            </DetailsItemValue>
          </DetailsItem>
        )}
        {claimedSpk.gt(NormalizedNumber.ZERO) && (
          <DetailsItem>
            <DetailsItemLabel text="Claimed SPK" />
            <DetailsItemValue
              icon={tokenLogos.spk}
              iconAlt="SPK icon"
              dataTestId={testIds.points.convertPointsPanel.claimedSpk}
            >
              <TokenAmountTooltip
                token={USD_MOCK_TOKEN}
                amount={claimedSpk}
                options={{ style: 'compact', showSymbol: 'never' }}
              />
            </DetailsItemValue>
          </DetailsItem>
        )}
      </div>

      <div className="flex flex-col gap-5">
        <div className="typography-body-3 text-tertiary">
          Earn Spark Points by taking real action. The more you participate and refer, the higher you climb.
        </div>
        <Actions
          onClaim={onClaim}
          claimableSpk={claimableSpk}
          convertPointsPanelAction={convertPointsPanelAction}
          onSwitchToMainnet={onSwitchToMainnet}
        />
      </div>
    </Panel>
  )
}

function DetailsItem({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-between gap-4">{children}</div>
}

function DetailsItemLabel({ text, explainer }: { text: string; explainer?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="typography-label-2">{text}</div>
      {explainer && <Info>{explainer}</Info>}
    </div>
  )
}

function DetailsItemValue({
  icon,
  iconAlt,
  children,
  dataTestId,
}: {
  icon: string
  iconAlt: string
  children: ReactNode
  dataTestId?: string
}) {
  return (
    <div className="flex items-center gap-1">
      <img src={icon} alt={iconAlt} className="icon-size-xxs" />
      <span className="typography-label-2" data-testid={dataTestId}>
        {children}
      </span>
    </div>
  )
}

function SeasonPointsItem({ label, amount }: { label: string; amount: NormalizedNumber }) {
  return (
    <DetailsItem>
      <DetailsItemLabel text={label} />
      <DetailsItemValue icon={assets.points.pointsIcon} iconAlt="Points icon">
        <TokenAmountTooltip
          token={USD_MOCK_TOKEN}
          amount={amount}
          options={{ style: 'compact', showSymbol: 'never' }}
        />
      </DetailsItemValue>
    </DetailsItem>
  )
}

function Actions({
  onClaim,
  claimableSpk,
  convertPointsPanelAction,
  onSwitchToMainnet,
}: Pick<ConvertPointsPanelProps, 'onClaim' | 'claimableSpk' | 'convertPointsPanelAction' | 'onSwitchToMainnet'>) {
  const canClaim = !!onClaim && claimableSpk.gt(NormalizedNumber.ZERO)

  if (canClaim && convertPointsPanelAction === 'switch-to-mainnet') {
    return (
      <Button variant="primary" size="l" onClick={onSwitchToMainnet}>
        Switch network and Claim
      </Button>
    )
  }

  return (
    <div className={cn('grid grid-cols-1 gap-4', canClaim && 'grid-cols-2')}>
      <ExternalLinkButton to={links.docs.points} variant="secondary" size="l">
        Learn more
        <ExternalLinkIcon className="icon-size-sm" />
      </ExternalLinkButton>
      {canClaim && (
        <Button variant="primary" size="l" onClick={onClaim}>
          <img src={assets.points.spkInvertedIcon} alt="SPK icon" className="icon-size-sm" />
          Claim SPK
        </Button>
      )}
    </div>
  )
}
